import React, { useState, useEffect, useRef } from 'react';

/**
 * Multi-select dropdown component
 * @param {string} label - Label text
 * @param {Array} options - Array of option values
 * @param {Array} selected - Array of selected values
 * @param {Function} onChange - Callback when selection changes
 * @param {string} placeholder - Placeholder text when nothing selected
 * @param {Function} renderOption - Optional function to render option text (receives option value)
 */
function MultiSelect({ label, options, selected, onChange, placeholder, renderOption }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const getOptionLabel = (option) => renderOption ? renderOption(option) : option;

  const displayText = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? getOptionLabel(selected[0])
      : `${selected.length} selected`;

  return (
    <div className="multi-select" ref={ref}>
      {label && <label>{label}</label>}
      <button
        type="button"
        className={`multi-select-trigger ${selected.length > 0 ? 'has-selection' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="multi-select-text">{displayText}</span>
        <span className="multi-select-arrow">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>
      {isOpen && (
        <div className="multi-select-dropdown">
          {selected.length > 0 && (
            <button
              type="button"
              className="multi-select-clear"
              onClick={() => onChange([])}
            >
              Clear all
            </button>
          )}
          {options.map(option => (
            <label key={option} className="multi-select-option">
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => toggleOption(option)}
              />
              <span>{getOptionLabel(option)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
